import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Folder, Save, X, Zap, MessageSquare } from 'lucide-react';
import { toast } from 'react-toastify';
import { 
  getMensagensPredefinidas, 
  getCategoriasMensagens,
  criarMensagemPredefinida,
  atualizarMensagemPredefinida,
  deletarMensagemPredefinida
} from '../services/api';
import './MensagensPredefinidas.css';

const MensagensPredefinidas = () => {
  const [mensagens, setMensagens] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [showModal, setShowModal] = useState(false);
  const [mensagemEditando, setMensagemEditando] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    conteudo: '',
    atalho: '',
    categoria: '',
    ativo: true
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [mensagensRes, categoriasRes] = await Promise.all([
        getMensagensPredefinidas(false, null, null),
        getCategoriasMensagens()
      ]);
      setMensagens(mensagensRes.data || []);
      setCategorias(categoriasRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar mensagens pré-definidas');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const abrirModalNova = () => {
    setMensagemEditando(null);
    setFormData({
      titulo: '',
      conteudo: '',
      atalho: '',
      categoria: '',
      ativo: true
    });
    setShowModal(true);
  };

  const abrirModalEditar = (mensagem) => {
    setMensagemEditando(mensagem);
    setFormData({
      titulo: mensagem.titulo,
      conteudo: mensagem.conteudo,
      atalho: mensagem.atalho || '',
      categoria: mensagem.categoria || '',
      ativo: mensagem.ativo
    });
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setMensagemEditando(null);
    setFormData({
      titulo: '',
      conteudo: '',
      atalho: '',
      categoria: '',
      ativo: true
    });
  };

  const salvarMensagem = async () => {
    if (!formData.titulo.trim() || !formData.conteudo.trim()) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }

    try {
      if (mensagemEditando) {
        await atualizarMensagemPredefinida(mensagemEditando.id, formData);
        toast.success('Mensagem atualizada com sucesso!');
      } else {
        await criarMensagemPredefinida(formData);
        toast.success('Mensagem criada com sucesso!');
      }
      fecharModal();
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar mensagem:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar mensagem');
    }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta mensagem?')) {
      return;
    }

    try {
      await deletarMensagemPredefinida(id);
      toast.success('Mensagem deletada com sucesso!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao deletar mensagem:', error);
      toast.error('Erro ao deletar mensagem');
    }
  };

  const toggleAtivo = async (mensagem) => {
    try {
      await atualizarMensagemPredefinida(mensagem.id, {
        ativo: !mensagem.ativo
      });
      toast.success(`Mensagem ${!mensagem.ativo ? 'ativada' : 'desativada'} com sucesso!`);
      carregarDados();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status da mensagem');
    }
  };

  const mensagensFiltradas = mensagens.filter(msg => {
    if (categoriaFiltro !== 'todas' && msg.categoria !== categoriaFiltro) {
      return false;
    }
    if (busca) {
      const termo = busca.toLowerCase();
      return (
        msg.titulo.toLowerCase().includes(termo) ||
        msg.conteudo.toLowerCase().includes(termo) ||
        (msg.atalho && msg.atalho.toLowerCase().includes(termo))
      );
    }
    return true;
  });

  return (
    <div className="mensagens-predefinidas-container">
      <div className="mensagens-predefinidas-header">
        <div>
          <h1 className="mensagens-predefinidas-title">Mensagens Pré-definidas</h1>
          <p className="mensagens-predefinidas-subtitle">
            Gerencie mensagens rápidas que os agentes podem usar no chat
          </p>
        </div>
        <button className="btn btn-primary" onClick={abrirModalNova}>
          <Plus size={18} />
          Nova Mensagem
        </button>
      </div>

      <div className="mensagens-predefinidas-filters">
        <div className="mensagens-predefinidas-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Buscar mensagens..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="mensagens-predefinidas-categoria-filter">
          <Folder size={18} />
          <select
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
          >
            <option value="todas">Todas as categorias</option>
            {categorias.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="mensagens-predefinidas-loading">Carregando...</div>
      ) : (
        <div className="mensagens-predefinidas-grid">
          {mensagensFiltradas.map((mensagem) => (
            <div
              key={mensagem.id}
              className={`mensagens-predefinidas-card ${!mensagem.ativo ? 'inativo' : ''}`}
            >
              <div className="mensagens-predefinidas-card-header">
                <div className="mensagens-predefinidas-card-title-section">
                  <MessageSquare size={18} />
                  <h3>{mensagem.titulo}</h3>
                  {!mensagem.ativo && (
                    <span className="mensagens-predefinidas-badge-inativo">Inativo</span>
                  )}
                </div>
                <div className="mensagens-predefinidas-card-actions">
                  <button
                    className="mensagens-predefinidas-btn-toggle"
                    onClick={() => toggleAtivo(mensagem)}
                    title={mensagem.ativo ? 'Desativar' : 'Ativar'}
                  >
                    {mensagem.ativo ? '✓' : '✗'}
                  </button>
                  <button
                    className="mensagens-predefinidas-btn-edit"
                    onClick={() => abrirModalEditar(mensagem)}
                    title="Editar"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="mensagens-predefinidas-btn-delete"
                    onClick={() => handleDeletar(mensagem.id)}
                    title="Deletar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {mensagem.categoria && (
                <div className="mensagens-predefinidas-card-categoria">
                  <Folder size={12} />
                  {mensagem.categoria}
                </div>
              )}

              {mensagem.atalho && (
                <div className="mensagens-predefinidas-card-atalho">
                  <Zap size={12} />
                  Atalho: {mensagem.atalho}
                </div>
              )}

              <div className="mensagens-predefinidas-card-conteudo">
                {mensagem.conteudo}
              </div>
            </div>
          ))}
        </div>
      )}

      {mensagensFiltradas.length === 0 && !loading && (
        <div className="mensagens-predefinidas-empty">
          <MessageSquare size={48} />
          <p>Nenhuma mensagem encontrada</p>
          <button className="btn btn-primary" onClick={abrirModalNova}>
            Criar primeira mensagem
          </button>
        </div>
      )}

      {showModal && (
        <div className="mensagens-predefinidas-modal-overlay" onClick={fecharModal}>
          <div className="mensagens-predefinidas-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mensagens-predefinidas-modal-header">
              <h2>{mensagemEditando ? 'Editar Mensagem' : 'Nova Mensagem'}</h2>
              <button className="mensagens-predefinidas-modal-close" onClick={fecharModal}>
                <X size={20} />
              </button>
            </div>

            <div className="mensagens-predefinidas-modal-body">
              <div className="mensagens-predefinidas-form-group">
                <label>Título *</label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleInputChange}
                  placeholder="Ex: Saudação Inicial"
                />
              </div>

              <div className="mensagens-predefinidas-form-group">
                <label>Conteúdo *</label>
                <textarea
                  name="conteudo"
                  value={formData.conteudo}
                  onChange={handleInputChange}
                  placeholder="Digite a mensagem... Você pode usar variáveis como {nome_cliente}, {protocolo}, {telefone}, {agente}, {data}, {hora}"
                  rows={6}
                />
                <small>
                  Variáveis disponíveis: {'{nome_cliente}'}, {'{protocolo}'}, {'{telefone}'}, {'{agente}'}, {'{data}'}, {'{hora}'}
                </small>
              </div>

              <div className="mensagens-predefinidas-form-row">
                <div className="mensagens-predefinidas-form-group">
                  <label>Atalho (opcional)</label>
                  <input
                    type="text"
                    name="atalho"
                    value={formData.atalho}
                    onChange={handleInputChange}
                    placeholder="Ex: /saudacao"
                  />
                </div>

                <div className="mensagens-predefinidas-form-group">
                  <label>Categoria (opcional)</label>
                  <input
                    type="text"
                    name="categoria"
                    value={formData.categoria}
                    onChange={handleInputChange}
                    placeholder="Ex: Saudações"
                    list="categorias-list"
                  />
                  <datalist id="categorias-list">
                    {categorias.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="mensagens-predefinidas-form-group">
                <label className="mensagens-predefinidas-checkbox-label">
                  <input
                    type="checkbox"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleInputChange}
                  />
                  <span>Mensagem ativa</span>
                </label>
              </div>
            </div>

            <div className="mensagens-predefinidas-modal-footer">
              <button className="btn btn-secondary" onClick={fecharModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={salvarMensagem}>
                <Save size={18} />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MensagensPredefinidas;

